import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { Link } from '@assets-components'
import { Image } from '@media-components'
import { ActionButton } from '@components/common/ActionButton'
import { StartRating } from '@components/common/StartRating'
import { CustomStyles, getStyles } from '@utils/styles'
import { CardData } from '../DefaultCard'
import defaultStyles, { StyleClassNames } from './styles'

export type CertificateSectionCardData = CardData

export type CertificateSectionCardProps = CertificateSectionCardData & {
  csx?: CustomStyles<StyleClassNames>
  darkBg?: boolean
}

const STAR_RATING_MIN = 0
const STAR_RATING_MAX = 5

/** Parses CMS label to a valid star rating (0-5). Returns null if invalid (e.g. "hello"). */
function getValidStarRating(label: string | undefined): number | null {
  if (label == null || String(label).trim() === '') return null
  const num = Number(label)
  if (Number.isNaN(num)) return null
  const clamped = Math.min(STAR_RATING_MAX, Math.max(STAR_RATING_MIN, num))
  return clamped
}

export function CertificateSectionCard({
  imageData,
  title,
  description,
  actionButtonData,
  href,
  csx,
  darkBg = false,
  statsData,
}: CertificateSectionCardProps) {
  const styles = getStyles(defaultStyles, csx)
  const rating = getValidStarRating(statsData?.[0]?.label)
  const cardHref = href || actionButtonData?.link
  const isLinkProvided = Boolean(cardHref)

  return (
    <Box
      {...styles('wrapper', {
        backgroundColor: darkBg ? 'background.paperDark' : 'background.paper',
        ...(isLinkProvided && {
          cursor: 'pointer',
          transition: 'box-shadow 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0px 8px 28px rgba(0, 0, 0, 0.18)',
          },
        }),
      })}
      {...(isLinkProvided && {
        component: Link,
        href: cardHref,
        'aria-label': title,
      })}
    >
      {imageData && <Image {...imageData} {...styles('image')} />}
      <Stack gap="16px" alignItems="center">
        <Stack gap="8px">
          {rating != null && (
            <StartRating
              rating={rating}
              subtitle={statsData?.[0]?.description}
              iconData={undefined}
            />
          )}
          <Typography
            variant="body1.600"
            color={darkBg ? 'text.primaryDark' : 'text.primary'}
            {...styles('title')}
          >
            {title}
          </Typography>
          {description && (
            <Typography
              variant="body1.300"
              color={darkBg ? 'text.secondaryDark' : 'text.secondary'}
              {...styles('description')}
            >
              {description}
            </Typography>
          )}
        </Stack>
        {actionButtonData && (
          <ActionButton
            {...actionButtonData}
            link={actionButtonData.link || href}
            variant="text"
            showArrow
            darkTheme={darkBg}
            {...styles('actionButton', {
              color: darkBg ? '#ffffff' : undefined,
              '.MuiButton-icon': {
                color: darkBg ? '#ffffff !important' : undefined,
              },
            })}
          />
        )}
      </Stack>
    </Box>
  )
}
